import { Component, inject, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core'
import { MatDialogRef, MatDialogContent, MatDialogActions, MatDialogTitle, MAT_DIALOG_DATA } from '@angular/material/dialog'
import { MatButton } from '@angular/material/button'
import { MatProgressSpinner } from '@angular/material/progress-spinner'
import { TranslatePipe } from '@ngx-translate/core'
import { FileUploadComponent, FileSizeLimitExceededEvent, PageLimitExceededEvent } from '../../../../file-upload/file-upload.component'
import { SentryService } from '../../../../sentry.service'
import { CallsService } from '../../../../calls.service'
import { DocumentUploadInfo } from '../../../../utils'
import { getUploadErrorMessage } from '../../../../uploads.service'

export type BoardPackIngestDialogInputData = {
  callId: number
  hasExistingBoardPack?: boolean
}

const INGEST_ERROR_MESSAGE_BY_CODE: Record<string, string> = {
  DOCUMENT_EXCEEDS_CONTENT_CAPACITY: 'CALLS.ADMIN.BOARD_PACK.TOO_LARGE',
  DOCUMENT_TOKEN_COUNT_FAILED: 'CALLS.ADMIN.BOARD_PACK.COUNT_FAILED',
  AGENDA_EXTRACTION_IN_PROGRESS: 'CALLS.ADMIN.BOARD_PACK.AGENDA_EXTRACTION_IN_PROGRESS',
}

@Component({
  selector: 'app-board-pack-ingest-dialog',
  imports: [MatDialogContent, MatDialogActions, MatDialogTitle, MatButton, MatProgressSpinner, TranslatePipe, FileUploadComponent],
  templateUrl: './board-pack-ingest-dialog.component.html',
  styleUrl: './board-pack-ingest-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export class BoardPackIngestDialogComponent {
  dialogRef = inject(MatDialogRef<BoardPackIngestDialogComponent>)
  private data = inject<BoardPackIngestDialogInputData>(MAT_DIALOG_DATA)
  private sentryService = inject(SentryService)
  private callsService = inject(CallsService)
  private changeDetectorRef = inject(ChangeDetectorRef)

  readonly MAX_BOARD_PACK_PAGES = 550
  readonly MAX_BOARD_PACK_UPLOAD_BYTES = 25_000_000

  uploadInfo: DocumentUploadInfo | null = null
  isSubmitting = false
  submitErrorMessage: string | null = null

  private ingestMutation = this.callsService.ingestBoardPackMutation()

  onFileChange(info: DocumentUploadInfo | null) {
    this.uploadInfo = info
    this.submitErrorMessage = null
    this.changeDetectorRef.detectChanges()
  }

  onPageLimitExceeded(event: PageLimitExceededEvent) {
    this.reportLimitExceeded(`Board pack exceeds maximum page count for ingestion for call ${this.data.callId}`, {
      pageCount: event.pageCount,
      maxPages: event.maxPages,
    })
  }

  onFileSizeLimitExceeded(event: FileSizeLimitExceededEvent) {
    this.reportLimitExceeded(`Board pack exceeds maximum file size for ingestion for call ${this.data.callId}`, {
      fileSize: event.fileSize,
      maxFileSize: event.maxFileSize,
    })
  }

  private reportLimitExceeded(message: string, extra: Record<string, number>) {
    this.sentryService.captureMessage(message, {
      level: 'error',
      tags: { feature: 'board-pack-ingestion' },
      extra,
    })
  }

  async submit(): Promise<void> {
    if (this.uploadInfo === null || this.isSubmitting) {
      return
    }
    this.isSubmitting = true
    this.submitErrorMessage = null
    this.changeDetectorRef.detectChanges()
    try {
      await this.ingestMutation.mutateAsync({ callId: this.data.callId, uploadKey: this.uploadInfo.key, fileName: this.uploadInfo.fileName })
      this.dialogRef.close(true)
    } catch (error) {
      const code = (error as { error?: { message?: string } })?.error?.message ?? ''
      this.submitErrorMessage = INGEST_ERROR_MESSAGE_BY_CODE[code] ?? getUploadErrorMessage(error) ?? 'CALLS.ADMIN.BOARD_PACK.INGEST_FAILED'
      this.isSubmitting = false
      this.changeDetectorRef.detectChanges()
    }
  }

  cancel() {
    if (this.isSubmitting) {
      return
    }
    this.dialogRef.close()
  }

  get isSubmitDisabled(): boolean {
    return this.uploadInfo === null || this.isSubmitting
  }

  get hasExistingBoardPack(): boolean {
    return this.data.hasExistingBoardPack ?? false
  }
}
